import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import BigButton from "../../components/Button/BigButton";
import { 
    getGraduationVideos,
    getGraduationPdfs,
    getVideoPresignedUploadUrl,
    getPdfPresignedUploadUrl,
    completeVideoUpload,
    completePdfUpload,
    getVideoDownloadUrl,
    getPdfDownloadUrl,
    deleteGraduationVideo,
    deleteGraduationPdf
} from '../../services/apis/StudioApi';
import axios from 'axios';

const GraduationAlbumPage = () => {
    const { schoolId } = useParams();
    const [albumData, setAlbumData] = useState({
        videos: [],
        pdfs: []
    });
    const [isLoading, setIsLoading] = useState(true);
    const [uploadProgress, setUploadProgress] = useState({ video: 0, pdf: 0 });
    const [uploadStatus, setUploadStatus] = useState({ video: '', pdf: '' });
    const [showTitleModal, setShowTitleModal] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileType, setFileType] = useState('');
    const [title, setTitle] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    useEffect(() => {
        fetchExistingFiles();
    }, [schoolId]);

    const fetchExistingFiles = async () => {
        try {
            setIsLoading(true);
            
            const [videosResponse, pdfsResponse] = await Promise.all([
                getGraduationVideos(schoolId).catch(() => ({ data: [] })),
                getGraduationPdfs(schoolId).catch(() => ({ data: [] }))
            ]);
            
            setAlbumData({
                videos: Array.isArray(videosResponse.data) ? videosResponse.data : [],
                pdfs: Array.isArray(pdfsResponse.data) ? pdfsResponse.data : []
            });
        } catch (error) {
            console.error('졸업앨범 파일 조회 실패:', error);
            setAlbumData({ videos: [], pdfs: [] });
        } finally {
            setIsLoading(false);
        }
    };

    const handleUploadPDF = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                setSelectedFile(file);
                setFileType('pdf');
                setTitle(`${new Date().getFullYear()}년 졸업앨범`);
                setShowTitleModal(true);
            }
        };
        input.click();
    };

    const handleUploadVideo = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.mp4,.mpeg,.mov,.avi,.wmv';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (file) {
                setSelectedFile(file);
                setFileType('video');
                setTitle(`${new Date().getFullYear()}년 졸업영상`);
                setShowTitleModal(true);
            }
        };
        input.click();
    };

    const handleConfirmUpload = async () => {
        if (!selectedFile || !title.trim()) return;
        
        setShowTitleModal(false);
        
        if (fileType === 'pdf') {
            await uploadPdf(selectedFile, title.trim());
        } else if (fileType === 'video') {
            await uploadVideo(selectedFile, title.trim());
        }
        
        // 상태 초기화
        setSelectedFile(null);
        setFileType('');
        setTitle('');
    };

    const handleCancelUpload = () => {
        setShowTitleModal(false);
        setSelectedFile(null);
        setFileType('');
        setTitle('');
    };

    const uploadVideo = async (file, title) => {
        try {
            setUploadProgress(prev => ({ ...prev, video: 0 }));
            setUploadStatus(prev => ({ ...prev, video: 'preparing' }));
            
            // 1. Presigned URL 요청
            setUploadStatus(prev => ({ ...prev, video: 'getting-url' }));
            const uploadData = {
                school_id: parseInt(schoolId),
                title: title,
                file_name: file.name,
                file_type: file.type,
                file_size: file.size
            };
            
            const presignedResponse = await getVideoPresignedUploadUrl(uploadData);
            const { upload_url, fields, key } = presignedResponse.data;
            
            // 2. S3에 파일 업로드
            setUploadStatus(prev => ({ ...prev, video: 'uploading' }));
            const formData = new FormData();
            Object.keys(fields).forEach(fieldKey => {
                formData.append(fieldKey, fields[fieldKey]);
            });
            formData.append('file', file);
            
            await axios.post(upload_url, formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(prev => ({ ...prev, video: percentCompleted }));
                }
            });
            
            // 3. 업로드 완료 처리
            setUploadStatus(prev => ({ ...prev, video: 'completing' }));
            const completeData = {
                key: key,
                title: title,
                file_size: file.size,
                duration: 0 // 실제로는 비디오 duration을 계산해야 함
            };
            
            await completeVideoUpload(completeData);
            
            // 4. 데이터 새로고침
            setUploadStatus(prev => ({ ...prev, video: 'refreshing' }));
            await fetchExistingFiles();
            
            alert('영상 파일이 성공적으로 업로드되었습니다.');
        } catch (error) {
            console.error('영상 업로드 실패:', error);
            alert('영상 업로드에 실패했습니다.');
        } finally {
            setUploadProgress(prev => ({ ...prev, video: 0 }));
            setUploadStatus(prev => ({ ...prev, video: '' }));
        }
    };

    const uploadPdf = async (file, title) => {
        try {
            setUploadProgress(prev => ({ ...prev, pdf: 0 }));
            setUploadStatus(prev => ({ ...prev, pdf: 'preparing' }));
            
            // 1. Presigned URL 요청
            setUploadStatus(prev => ({ ...prev, pdf: 'getting-url' }));
            const uploadData = {
                school_id: parseInt(schoolId),
                title: title,
                file_name: file.name,
                file_type: file.type,
                file_size: file.size
            };
            
            const presignedResponse = await getPdfPresignedUploadUrl(uploadData);
            const { upload_url, fields, key } = presignedResponse.data;
            
            // 2. S3에 파일 업로드
            setUploadStatus(prev => ({ ...prev, pdf: 'uploading' }));
            const formData = new FormData();
            Object.keys(fields).forEach(fieldKey => {
                formData.append(fieldKey, fields[fieldKey]);
            });
            formData.append('file', file);
            
            await axios.post(upload_url, formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(prev => ({ ...prev, pdf: percentCompleted }));
                }
            });
            
            // 3. 업로드 완료 처리
            setUploadStatus(prev => ({ ...prev, pdf: 'completing' }));
            const completeData = {
                key: key,
                title: title,
                file_size: file.size,
                page_count: 0 // 실제로는 PDF 페이지 수를 계산해야 함
            };
            
            await completePdfUpload(completeData);
            
            // 4. 데이터 새로고침
            setUploadStatus(prev => ({ ...prev, pdf: 'refreshing' }));
            await fetchExistingFiles();
            
            alert('PDF 파일이 성공적으로 업로드되었습니다.');
        } catch (error) {
            console.error('PDF 업로드 실패:', error);
            alert('PDF 업로드에 실패했습니다.');
        } finally {
            setUploadProgress(prev => ({ ...prev, pdf: 0 }));
            setUploadStatus(prev => ({ ...prev, pdf: '' }));
        }
    };

    const handleViewPDF = async (pdfId) => {
        try {
            const response = await getPdfDownloadUrl(pdfId);
            window.open(response.data.download_url, '_blank');
        } catch (error) {
            console.error('PDF 보기 실패:', error);
            alert('PDF 파일을 불러올 수 없습니다.');
        }
    };

    const handleViewVideo = async (videoId) => {
        try {
            const response = await getVideoDownloadUrl(videoId);
            window.open(response.data.download_url, '_blank');
        } catch (error) {
            console.error('영상 보기 실패:', error);
            alert('영상 파일을 불러올 수 없습니다.');
        }
    };

    const handleDeletePdf = (pdfId, title) => {
        setDeleteTarget({ id: pdfId, title, type: 'pdf' });
        setShowDeleteModal(true);
    };

    const handleDeleteVideo = (videoId, title) => {
        setDeleteTarget({ id: videoId, title, type: 'video' });
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        
        try {
            if (deleteTarget.type === 'pdf') {
                await deleteGraduationPdf(deleteTarget.id);
            } else {
                await deleteGraduationVideo(deleteTarget.id);
            }
            
            await fetchExistingFiles(); // 목록 새로고침
            alert(`${deleteTarget.type === 'pdf' ? 'PDF' : '영상'} 파일이 성공적으로 삭제되었습니다.`);
        } catch (error) {
            console.error('파일 삭제 실패:', error);
            alert('파일 삭제에 실패했습니다.');
        } finally {
            setShowDeleteModal(false);
            setDeleteTarget(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setDeleteTarget(null);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('ko-KR');
    };

    const formatFileName = (filePath) => {
        if (!filePath) return '알 수 없음';
        
        try {
            // 파일 경로에서 파일명만 추출
            const fileName = filePath.split('/').pop();
            // URL 디코딩
            const decodedFileName = decodeURIComponent(fileName);
            
            // 파일명이 너무 길면 축약
            if (decodedFileName.length > 50) {
                const extension = decodedFileName.split('.').pop();
                const nameWithoutExt = decodedFileName.replace(`.${extension}`, '');
                return `${nameWithoutExt.substring(0, 30)}...${nameWithoutExt.substring(nameWithoutExt.length - 10)}.${extension}`;
            }
            
            return decodedFileName;
        } catch (error) {
            // 디코딩 실패 시 원본 파일명 반환
            const fileName = filePath.split('/').pop();
            return fileName.length > 30 ? `${fileName.substring(0, 30)}...` : fileName;
        }
    };

    const getStatusMessage = (status) => {
        switch (status) {
            case 'preparing':
                return '업로드 준비 중...';
            case 'getting-url':
                return '업로드 URL 생성 중...';
            case 'uploading':
                return '파일 업로드 중...';
            case 'completing':
                return '업로드 완료 처리 중...';
            case 'refreshing':
                return '목록 새로고침 중...';
            default:
                return '';
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg">로딩 중...</div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <h1 className="text-2xl font-bold text-center mb-8">졸업앨범 관리</h1>

            {/* 제목 입력 모달 */}
            {showTitleModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96 max-w-lg mx-4">
                        <h3 className="text-lg font-semibold mb-4">
                            {fileType === 'pdf' ? 'PDF' : '영상'} 제목 입력
                        </h3>
                        
                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                                선택한 파일: {selectedFile?.name}
                            </p>
                            <p className="text-sm text-gray-600 mb-4">
                                파일 크기: {selectedFile ? formatFileSize(selectedFile.size) : ''}
                            </p>
                            
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                제목
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && title.trim()) {
                                        handleConfirmUpload();
                                    } else if (e.key === 'Escape') {
                                        handleCancelUpload();
                                    }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={`${new Date().getFullYear()}년 ${fileType === 'pdf' ? '졸업앨범' : '졸업영상'}`}
                                autoFocus
                            />
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={handleCancelUpload}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            >
                                취소
                            </button>
                            <button
                                onClick={handleConfirmUpload}
                                disabled={!title.trim()}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                업로드 시작
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 삭제 확인 모달 */}
            {showDeleteModal && deleteTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96 max-w-lg mx-4">
                        <div className="flex items-center mb-4">
                            <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900">
                                {deleteTarget.type === 'pdf' ? 'PDF' : '영상'} 파일 삭제
                            </h3>
                        </div>
                        
                        <div className="mb-6">
                            <p className="text-sm text-gray-600 mb-3">
                                다음 파일을 삭제하시겠습니까?
                            </p>
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <p className="font-medium text-gray-900">{deleteTarget.title}</p>
                            </div>
                            
                            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <div className="flex items-start">
                                    <svg className="w-5 h-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <div>
                                        <p className="text-sm font-medium text-red-800">주의사항</p>
                                        <p className="text-sm text-red-700 mt-1">
                                            삭제된 파일은 복구가 불가능합니다. 신중하게 결정해 주세요.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={cancelDelete}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            >
                                취소
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            >
                                삭제하기
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* PDF 섹션 */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold mr-6">PDF 파일</h2>
                    <BigButton 
                        handleClick={handleUploadPDF} 
                        label="+ PDF 업로드" 
                        className="bg-green-500 hover:bg-green-600 text-sm px-4 py-2"
                    />
                </div>
                
                {uploadStatus.pdf && (
                    <div className="mb-4">
                        <div className="bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                style={{ width: uploadStatus.pdf === 'uploading' ? `${uploadProgress.pdf}%` : '100%' }}
                            ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                            {getStatusMessage(uploadStatus.pdf)}
                            {uploadStatus.pdf === 'uploading' && ` ${uploadProgress.pdf}%`}
                        </p>
                    </div>
                )}
                
                {Array.isArray(albumData.pdfs) && albumData.pdfs.length > 0 ? (
                    <div className="space-y-4">
                        {albumData.pdfs.map((pdf) => (
                            <div key={pdf.id} className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1">
                                        <p className="font-medium text-lg mb-2">{pdf.title}</p>
                                        <div className="space-y-1">
                                            <p className="text-sm text-gray-600" title={pdf.pdf_file ? decodeURIComponent(pdf.pdf_file.split('/').pop()) : ''}>
                                                파일명: {formatFileName(pdf.pdf_file)}
                                            </p>
                                            <p className="text-sm text-gray-600">업로드: {formatDate(pdf.created_at)}</p>
                                            <p className="text-sm text-gray-600">크기: {formatFileSize(pdf.file_size)}</p>
                                            {pdf.page_count > 0 && (
                                                <p className="text-sm text-gray-600">페이지: {pdf.page_count}쪽</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col space-y-2 min-w-0">
                                        <button 
                                            onClick={() => handleViewPDF(pdf.id)}
                                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1.5 rounded whitespace-nowrap transition-colors"
                                        >
                                            보기
                                        </button>
                                        <button 
                                            onClick={() => handleDeletePdf(pdf.id, pdf.title)}
                                            className="bg-transparent hover:bg-red-50 text-red-600 border border-red-500 hover:border-red-600 text-xs px-3 py-1.5 rounded whitespace-nowrap transition-colors"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-600">업로드된 PDF 파일이 없습니다.</p>
                    </div>
                )}
            </div>

            {/* 영상 섹션 */}
            <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold mr-6">영상 파일</h2>
                    <BigButton 
                        handleClick={handleUploadVideo} 
                        label="+ 영상 업로드" 
                        className="bg-green-500 hover:bg-green-600 text-sm px-4 py-2"
                    />
                </div>
                
                {uploadStatus.video && (
                    <div className="mb-4">
                        <div className="bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                                style={{ width: uploadStatus.video === 'uploading' ? `${uploadProgress.video}%` : '100%' }}
                            ></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                            {getStatusMessage(uploadStatus.video)}
                            {uploadStatus.video === 'uploading' && ` ${uploadProgress.video}%`}
                        </p>
                    </div>
                )}
                
                {Array.isArray(albumData.videos) && albumData.videos.length > 0 ? (
                    <div className="space-y-4">
                        {albumData.videos.map((video) => (
                            <div key={video.id} className="bg-gray-50 p-4 rounded-lg">
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1">
                                        <p className="font-medium text-lg mb-2">{video.title}</p>
                                        <div className="space-y-1">
                                            <p className="text-sm text-gray-600" title={video.video_file ? decodeURIComponent(video.video_file.split('/').pop()) : ''}>
                                                파일명: {formatFileName(video.video_file)}
                                            </p>
                                            <p className="text-sm text-gray-600">업로드: {formatDate(video.created_at)}</p>
                                            <p className="text-sm text-gray-600">크기: {formatFileSize(video.file_size)}</p>
                                            {video.duration > 0 && (
                                                <p className="text-sm text-gray-600">
                                                    재생시간: {Math.floor(video.duration / 60)}분 {video.duration % 60}초
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col space-y-2 min-w-0">
                                        <button 
                                            onClick={() => handleViewVideo(video.id)}
                                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1.5 rounded whitespace-nowrap transition-colors"
                                        >
                                            보기
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteVideo(video.id, video.title)}
                                            className="bg-transparent hover:bg-red-50 text-red-600 border border-red-500 hover:border-red-600 text-xs px-3 py-1.5 rounded whitespace-nowrap transition-colors"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <p className="text-gray-600">업로드된 영상 파일이 없습니다.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GraduationAlbumPage;